import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAgent,
  listMemoryEntries,
  deleteMemoryEntry,
  clearMemoryEntries,
  listAgentSkills,
  bindAgentSkill,
  unbindAgentSkill,
  type Agent,
  type MemoryEntry,
  type AgentSkillBinding,
} from "../api/client.js";
import { AgentConfigEditor } from "./AgentConfigEditor.js";
import { SkillPickerModal } from "./SkillPickerModal.js";

export function AgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"config" | "skills" | "policy" | "memory" | "logs">("config");

  useEffect(() => {
    if (!agentId) return;
    getAgent(agentId)
      .then(setAgent)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-sm font-semibold text-ink-800">Agent not found</h2>
          <button onClick={() => navigate("/agents")} className="btn-secondary mt-3 text-xs">
            Back to agents
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "config" as const, label: "Configuration" },
    { id: "skills" as const, label: "Skills" },
    { id: "policy" as const, label: "Tool Policy" },
    { id: "memory" as const, label: "Memory" },
    { id: "logs" as const, label: "Activity" },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 border-b border-ink-100 bg-white px-6 pb-0 pt-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/agents")}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-400 hover:bg-surface-2 hover:text-ink-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M10 4L6 8l4 4" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-semibold text-ink-900">{agent.name}</h1>
              <span className={agent.enabled ? "badge-green" : "badge-gray"}>
                {agent.enabled ? "Active" : "Disabled"}
              </span>
            </div>
            {agent.description && (
              <p className="mt-0.5 text-xs text-ink-500">{agent.description}</p>
            )}
          </div>
          <KillSwitch agentId={agent.id} enabled={agent.enabled} />
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-t-lg px-4 py-2 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-surface-1 text-ink-900"
                  : "text-ink-500 hover:text-ink-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto bg-surface-1">
        <div className="mx-auto max-w-3xl px-6 py-6">
          {activeTab === "config" && (
            <AgentConfigEditor agent={agent} onSave={setAgent} />
          )}
          {activeTab === "skills" && <SkillsTab agentId={agent.id} />}
          {activeTab === "policy" && <PolicyTab agent={agent} />}
          {activeTab === "memory" && <MemoryTab agentId={agent.id} />}
          {activeTab === "logs" && <ActivityTab agent={agent} />}
        </div>
      </div>
    </div>
  );
}

function SkillsTab({ agentId }: { agentId: string }) {
  const [skills, setSkills] = useState<AgentSkillBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  function refresh() {
    setLoading(true);
    listAgentSkills(agentId)
      .then((res) => setSkills(res.skills))
      .catch(() => setSkills([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, [agentId]);

  async function handleBind(skillId: string) {
    await bindAgentSkill(agentId, skillId);
    refresh();
  }

  async function handleUnbind(skillId: string) {
    await unbindAgentSkill(agentId, skillId);
    setSkills((prev) => prev.filter((s) => s.id !== skillId));
  }

  const riskColors: Record<string, string> = {
    low: "badge-green",
    medium: "badge-amber",
    high: "badge-red",
    critical: "badge-red",
  };

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Bound Skills</h3>
            <p className="mt-1 text-xs text-ink-500">
              Skills inject domain knowledge into this agent's system prompt. Only approved skills can be bound.
            </p>
          </div>
          <button onClick={() => setShowPicker(true)} className="btn-primary text-xs">
            Add Skill
          </button>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
        </div>
      ) : skills.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-2">
              <svg className="h-5 w-5 text-ink-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                <path d="M5 5h6M5 8h4" />
              </svg>
            </div>
            <p className="text-xs text-ink-500">No skills bound to this agent</p>
            <button onClick={() => setShowPicker(true)} className="btn-secondary mt-3 text-xs">
              Add a Skill
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {skills.map((skill) => (
            <div key={skill.id} className="card px-5 py-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-2">
                  <svg className="h-5 w-5 text-ink-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                    <path d="M5 5h6M5 8h4" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink-900">{skill.name}</span>
                    <span className={riskColors[skill.riskLevel] ?? "badge-gray"}>
                      {skill.riskLevel}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-2xs text-ink-400">
                    <span className="font-mono">{skill.skillId}</span>
                    <span>v{skill.version}</span>
                    {skill.author && <span>by {skill.author}</span>}
                  </div>
                  {skill.tools.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {skill.tools.map((tool) => (
                        <span key={tool} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-2xs text-ink-600">
                          {tool}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleUnbind(skill.id)}
                  className="btn-ghost text-xs text-danger hover:bg-red-50"
                  title="Remove skill"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showPicker && (
        <SkillPickerModal
          agentId={agentId}
          boundSkills={skills}
          onBind={handleBind}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function PolicyTab({ agent }: { agent: Agent }) {
  return (
    <div className="space-y-6">
      <section className="card p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Tool Policy Profile</h3>
        <div className="mt-3">
          <span className="badge-blue">{agent.toolPolicyProfile}</span>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-ink-500">
          The tool policy profile controls which tools this agent can access and what approval level
          is required for high-risk operations. Policies are defined in <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-2xs">config/policy.yaml</code>.
        </p>
      </section>

      <section className="card p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Security Controls</h3>
        <div className="mt-3 space-y-3">
          <SecurityRow label="Prompt injection detection" status="active" />
          <SecurityRow label="Output guard filtering" status="active" />
          <SecurityRow label="Behavioral monitoring" status="active" />
          <SecurityRow label="Container sandbox" status="active" />
          <SecurityRow label="Audit logging" status="active" />
        </div>
      </section>
    </div>
  );
}

function SecurityRow({ label, status }: { label: string; status: "active" | "inactive" }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2">
      <span className="text-sm text-ink-700">{label}</span>
      <span className={status === "active" ? "badge-green" : "badge-gray"}>
        {status}
      </span>
    </div>
  );
}

function MemoryTab({ agentId }: { agentId: string }) {
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState<string>("");
  const [confirmClear, setConfirmClear] = useState(false);

  function refresh() {
    setLoading(true);
    listMemoryEntries(agentId, userFilter || undefined)
      .then((res) => setEntries(res.entries))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, [agentId, userFilter]);

  async function handleDelete(userId: string, key: string) {
    await deleteMemoryEntry(agentId, userId, key);
    refresh();
  }

  async function handleClearAll() {
    await clearMemoryEntries(agentId, userFilter || undefined);
    setConfirmClear(false);
    refresh();
  }

  // Get unique user IDs for the filter
  const uniqueUsers = [...new Set(entries.map((e) => e.userId))];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <section className="card p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Memory Entries</h3>
          <div className="flex items-center gap-2">
            {confirmClear ? (
              <>
                <span className="text-xs text-red-600">Clear all entries?</span>
                <button
                  onClick={handleClearAll}
                  className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
                >
                  Yes
                </button>
                <button
                  onClick={() => setConfirmClear(false)}
                  className="rounded bg-ink-200 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-300"
                >
                  No
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmClear(true)}
                disabled={entries.length === 0}
                className="btn-secondary text-xs disabled:opacity-50"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* User filter */}
        {uniqueUsers.length > 1 && (
          <div className="mt-3">
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="input text-xs"
            >
              <option value="">All users</option>
              {uniqueUsers.map((uid) => (
                <option key={uid} value={uid}>
                  {uid.slice(0, 8)}...
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      {/* Entry table */}
      <section className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-xs text-ink-400">No memory entries found</p>
          </div>
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-ink-100 bg-surface-2">
                <th className="px-4 py-2.5 font-medium text-ink-500">Key</th>
                <th className="px-4 py-2.5 font-medium text-ink-500">Value</th>
                <th className="px-4 py-2.5 font-medium text-ink-500">Expires</th>
                <th className="px-4 py-2.5 font-medium text-ink-500">Updated</th>
                <th className="px-4 py-2.5 font-medium text-ink-500 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={`${entry.userId}-${entry.key}`} className="border-b border-ink-50 hover:bg-surface-1">
                  <td className="px-4 py-2.5 font-mono text-ink-800">{entry.key}</td>
                  <td className="max-w-xs truncate px-4 py-2.5 text-ink-600">
                    {entry.value.length > 100 ? entry.value.slice(0, 100) + "..." : entry.value}
                  </td>
                  <td className="px-4 py-2.5 text-ink-500">
                    {entry.expiresAt
                      ? new Date(entry.expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "Never"}
                  </td>
                  <td className="px-4 py-2.5 text-ink-500">
                    {new Date(entry.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => handleDelete(entry.userId, entry.key)}
                      className="text-red-400 hover:text-red-600"
                      title="Delete entry"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Summary */}
      {entries.length > 0 && (
        <p className="text-center text-xs text-ink-400">
          {entries.length} {entries.length === 1 ? "entry" : "entries"}{" "}
          {uniqueUsers.length > 0 && `across ${uniqueUsers.length} ${uniqueUsers.length === 1 ? "user" : "users"}`}
        </p>
      )}
    </div>
  );
}

function ActivityTab({ agent }: { agent: Agent }) {
  return (
    <div className="card p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Recent Activity</h3>
      <div className="mt-4 flex items-center justify-center py-8">
        <p className="text-xs text-ink-400">Activity logs will appear here during conversations</p>
      </div>
    </div>
  );
}

function KillSwitch({ agentId, enabled }: { agentId: string; enabled: boolean }) {
  const [isEnabled, setIsEnabled] = useState(enabled);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-ink-500">Kill Switch</span>
      <button
        onClick={() => setIsEnabled(!isEnabled)}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          isEnabled ? "bg-emerald-500" : "bg-ink-300"
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
            isEnabled ? "left-[1.375rem]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}
